import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, FileText, Search, ChevronRight, Copy } from 'lucide-react';

import { QuoteLayout } from '@/components/quotes/QuoteLayout';
import { QuotaBadge } from '@/components/quotes/QuotaBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatMinor } from '@/lib/quoteMoney';
import { QuoteStatusBadge } from './quoteStatus';

const API_URL = process.env.REACT_APP_API_URL;

export function QuotesList() {
  const { t, i18n } = useTranslation('quotes');
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [copyingId, setCopyingId] = useState(null);

  const load = async (selectedStatus = statusFilter) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/quotes`, {
        params: { q: query || undefined, status: selectedStatus || undefined },
      });
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const copy = async (quoteId) => {
    if (copyingId) return;
    setCopyingId(quoteId);
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/quotes/${quoteId}/copy`);
      navigate(`/quotes/${data.id}/edit`);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || t('common.error'));
    } finally { setCopyingId(null); }
  };

  const empty = !loading && items.length === 0;
  const lang = (i18n.resolvedLanguage || i18n.language || 'tr').split('-')[0];
  const locale = {
    tr: 'tr-TR', en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES',
  }[lang] || 'en-US';
  const formatDate = (value) => value ? new Date(value).toLocaleDateString(locale) : '—';

  return (
    <QuoteLayout>
      <div className="max-w-6xl mx-auto" data-testid="quotes-list-page">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A2E]" data-testid="quotes-page-title">
              {t('list.title')}
            </h1>
            <p className="text-sm text-[#595959] mt-1">{t('moduleTitle')}</p>
            <div className="mt-2"><QuotaBadge /></div>
          </div>
          <Button
            onClick={() => navigate('/quotes/new')}
            className="w-full bg-[#2563EB] hover:bg-[#1E4ED8] sm:w-auto"
            data-testid="new-quote-btn"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
            {t('list.newQuote')}
          </Button>
        </div>

        <form onSubmit={onSearch} className="mb-4 flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('common.search')}
              className="pl-9"
              data-testid="quotes-search-input"
            />
          </div>
          <Button type="submit" variant="outline" className="w-full sm:w-auto" data-testid="quotes-search-btn">
            {t('common.search')}
          </Button>
        </form>

        <div className="mb-4 flex flex-wrap gap-2" data-testid="quote-status-filters">
          {['', 'draft', 'sent', 'viewed', 'accepted', 'revision_requested', 'rejected'].map((status) => (
            <button type="button" key={status || 'all'} onClick={() => setStatusFilter(status)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${statusFilter === status ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600'}`}>
              {status ? t(`list.status.${status}`) : t('list.allStatuses')}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#595959]" data-testid="quotes-loading">
              {t('common.loading')}
            </div>
          ) : empty ? (
            <div className="p-12 text-center" data-testid="quotes-empty">
              <FileText className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[#1A1A2E] font-medium mb-1">{t('list.empty')}</p>
              <p className="text-sm text-[#94A3B8] mb-4">{t('list.emptyHint')}</p>
              <Button
                onClick={() => navigate('/quotes/new')}
                className="bg-[#2563EB] hover:bg-[#1E4ED8]"
                data-testid="empty-new-quote-btn"
              >
                <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
                {t('list.newQuote')}
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-3 p-3 md:hidden" data-testid="quotes-mobile-list">
                {items.map((q) => (
                  <div key={q.id} className="rounded-xl border border-slate-200 bg-white shadow-sm">
                    <button
                      type="button"
                      onClick={() => navigate('/quotes/' + q.id)}
                      className="w-full p-4 text-left transition-colors active:bg-slate-50"
                      data-testid={'quote-card-' + q.quote_number}
                    >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-blue-600">{q.quote_number}</div>
                        <h2 className="mt-1 break-words font-semibold leading-5 text-slate-900">{q.title}</h2>
                      </div>
                      <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-slate-300" />
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3">
                      <div className="min-w-0">
                        <div className="text-[11px] text-slate-400">{t('list.columns.customer')}</div>
                        <div className="mt-0.5 truncate text-sm text-slate-700">{q.customer_snapshot?.name || t('list.noCustomer')}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[11px] text-slate-400">{t('list.columns.subtotal')}</div>
                        <div className="mt-0.5 text-sm font-semibold text-slate-900">{formatMinor(q.subtotal_minor, q.currency)}</div>
                      </div>
                      <div>
                        <div className="text-[11px] text-slate-400">{t('list.columns.createdAt')}</div>
                        <div className="mt-0.5 text-sm text-slate-600">{formatDate(q.created_at)}</div>
                      </div>
                      <div className="text-right">
                        <QuoteStatusBadge status={q.status} t={t} />
                      </div>
                    </div>
                    </button>
                    <button type="button" onClick={() => copy(q.id)} disabled={!!copyingId}
                      className="flex items-center gap-1 px-4 pb-3 text-xs font-medium text-blue-600 disabled:opacity-50"
                      data-testid={`copy-quote-${q.id}`}>
                      <Copy className="h-3.5 w-3.5" /> {t('list.copyQuote')}
                    </button>
                  </div>
                ))}
              </div>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] text-[#475569]">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">{t('list.columns.quoteNumber')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('list.columns.title')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('list.columns.customer')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('list.columns.createdAt')}</th>
                    <th className="text-right px-4 py-3 font-medium">{t('list.columns.subtotal')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('list.columns.status')}</th>
                    <th className="px-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {items.map((q) => (
                    <tr
                      key={q.id}
                      onClick={() => navigate(`/quotes/${q.id}`)}
                      className="cursor-pointer hover:bg-[#F8FAFC]"
                      data-testid={`quote-row-${q.quote_number}`}
                    >
                      <td className="px-4 py-3 font-medium text-[#1A1A2E]">{q.quote_number}</td>
                      <td className="px-4 py-3 text-[#1A1A2E]">{q.title}</td>
                      <td className="px-4 py-3 text-[#595959]">
                        {q.customer_snapshot?.name || <span className="italic text-[#94A3B8]">{t('list.noCustomer')}</span>}
                      </td>
                      <td className="px-4 py-3 text-[#94A3B8]">
                        {formatDate(q.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right text-[#1A1A2E] font-medium">
                        {formatMinor(q.subtotal_minor, q.currency)}
                      </td>
                      <td className="px-4 py-3">
                        <QuoteStatusBadge status={q.status} t={t} />
                      </td>
                      <td className="px-2">
                        <button type="button" onClick={(e) => { e.stopPropagation(); copy(q.id); }}
                          disabled={!!copyingId} className="flex items-center gap-1 whitespace-nowrap text-xs text-blue-600 disabled:opacity-50"
                          data-testid={`copy-quote-${q.id}`}>
                          <Copy className="h-3.5 w-3.5" /> {t('list.copyQuote')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </QuoteLayout>
  );
}

export default QuotesList;
